import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../config/firebase";
import { Blog } from "../types/blog";

const BLOGS_COLLECTION = "blogs";

export const uploadImage = async (file: File, folder = "blogs") => {
  const timestamp = Date.now();
  const fileExt = file.name.split(".").pop();
  const safeFileName = `${timestamp}-${Math.random()
    .toString(36)
    .substring(2, 8)}.${fileExt}`;
  const storageRef = ref(storage, `${folder}/${safeFileName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

export const addBlog = async (blogData: Blog) => {
  const data = {
    ...blogData,
    createdAt: new Date().toISOString(),
  };

  const docRef = await addDoc(collection(db, BLOGS_COLLECTION), data);
  return { id: docRef.id };
};

export const fetchBlogs = async () => {
  const snapshot = await getDocs(collection(db, BLOGS_COLLECTION));
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Blog[];
};

export const deleteBlog = async (blogId: string) => {
  await deleteDoc(doc(db, BLOGS_COLLECTION, blogId));
  return { success: true };
};

export const updateBlog = async (
  blogId: string,
  updatedData: Partial<Blog>
) => {
  try {
    const blogRef = doc(db, BLOGS_COLLECTION, blogId);
    const docSnap = await getDoc(blogRef);

    if (!docSnap.exists()) {
      throw new Error(`No blog found with ID: ${blogId}`);
    }

    const { id, ...dataToUpdate } = updatedData;

    await updateDoc(blogRef, dataToUpdate);
    return { success: true };
  } catch (error) {
    console.error("Failed to update blog:", error);
    return { success: false, error };
  }
};

export const getBlogById = async (blogId: string) => {
  const blogRef = doc(db, BLOGS_COLLECTION, blogId);
  const docSnap = await getDoc(blogRef);

  if (!docSnap.exists()) throw new Error("Blog not found");

  return { id: docSnap.id, ...docSnap.data() } as Blog;
};

export const getBlogBySlug = async (slug: string) => {
  const q = query(collection(db, BLOGS_COLLECTION), where("slug", "==", slug));
  const snapshot = await getDocs(q);

  if (snapshot.empty) throw new Error("No article with this slug");

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as Blog;
};
